import type { ReportsQueryScope } from '../../reports/domain/reportsQueryScope';
import type { SaleStatusFilter, SaleFiscalStatusFilter, SaleSortKey, SaleSortDir, SaleCommissionFilter } from '../domain/salesTypes';
import type { SaleChannelFilter } from './salesListFilters';
import { fetchRemoteListSales } from '../../reports/infra/reportsRemoteSalesListRepo';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import type { SaleListRow } from './salesRepo';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';

export const SALES_LIST_EXPORT_MAX_ROWS = 2000;

export type FetchAllSalesListRowsParams = {
  branchId: string;
  fromIso: string;
  toIso: string;
  search?: string;
  cashierUserIdFilter?: string;
  primaryStaffUserIdFilter?: string;
  paymentPendingOnly?: boolean;
  queryScope?: ReportsQueryScope | null;
  pageSize: number;
  statusFilter?: SaleStatusFilter;
  paymentMethodFilter?: string;
  fiscalStatusFilter?: SaleFiscalStatusFilter;
  channelFilter?: SaleChannelFilter;
  commissionFilter?: SaleCommissionFilter;
  sortKey?: SaleSortKey;
  sortDir?: SaleSortDir;
};

export function mapExportParamsToRemoteListSales(
  params: FetchAllSalesListRowsParams,
  searchTrim: string,
  offset: number
) {
  return {
    branchId: params.branchId,
    fromIso: params.fromIso,
    toIso: params.toIso,
    search: searchTrim || undefined,
    limit: params.pageSize,
    offset,
    cashierUserIdFilter: params.cashierUserIdFilter,
    primaryStaffUserIdFilter: params.primaryStaffUserIdFilter,
    paymentPendingOnly: params.paymentPendingOnly,
    queryScope: params.queryScope,
    statusFilter: params.statusFilter,
    paymentMethodFilter: params.paymentMethodFilter,
    fiscalStatusFilter: params.fiscalStatusFilter,
    channelFilter: params.channelFilter,
    commissionFilter: params.commissionFilter,
    sortKey: params.sortKey,
    sortDir: params.sortDir,
  };
}

export function appendPageRowsForExport<T>(
  collected: T[],
  pageRows: T[],
  maxRows: number
): { rows: T[]; truncated: boolean } {
  const next = [...collected];
  let truncated = false;
  for (const row of pageRows) {
    if (next.length >= maxRows) {
      truncated = true;
      break;
    }
    next.push(row);
  }
  return { rows: next, truncated };
}

export async function fetchAllSalesListRowsForExport(
  params: FetchAllSalesListRowsParams
): Promise<{ rows: SaleListRow[]; truncated: boolean }> {
  const searchTrim = (params.search ?? '').trim();
  const collected: SaleListRow[] = [];
  let offset = 0;
  let truncated = false;

  const online = await isOnline();

  if (online) {
    while (collected.length < SALES_LIST_EXPORT_MAX_ROWS) {
      let remote;
      try {
        remote = await fetchRemoteListSales(
          mapExportParamsToRemoteListSales(params, searchTrim, offset)
        );
      } catch (e) {
        captureOpsFailure({
          module: 'sales',
          op: 'sales.export_list_page',
          error: e,
          ingestError: e instanceof Error ? e.message : null,
          applied: false,
          extra: { branchId: params.branchId, offset },
        });
        break;
      }
      if (!remote || remote.rows.length === 0) break;

      const { rows: mergedPage, truncated: pageTruncated } = appendPageRowsForExport(
        collected,
        remote.rows,
        SALES_LIST_EXPORT_MAX_ROWS
      );
      collected.length = 0;
      collected.push(...mergedPage);
      if (pageTruncated) truncated = true;

      if (truncated || !remote.hasMore) break;
      offset += params.pageSize;
    }
    return { rows: collected, truncated };
  }

  // ADR-017 / legacy-noop: no exportar lista vacía fingiendo “sin ventas” offline.
  throw new Error('Para exportar el historial necesitás conexión.');
}
